import { createContext, HTMLAttributes, useContext } from "react";
import cx from "classnames";

type RootProps = HTMLAttributes<HTMLDivElement> & {
  variant?: "default" | "secondary" | "primary";
  shadow?: boolean;
  borderRounded?: "sm" | "lg" | "xl";
};

const Context = createContext<Pick<RootProps, "shadow" | "variant">>({
  variant: "default",
  shadow: false,
});

const Provider = Context.Provider;

const Root = ({
  variant = "default",
  borderRounded = "sm",
  shadow = false,
  ...rest
}: RootProps) => {
  return (
    <Provider value={{ variant }}>
      <div
        {...rest}
        className={cx(
          "t-flex t-flex-col t-border t-border-solid t-border-neutral-10 t-overflow-auto t-relative",
          rest.className || "",
          {
            "t-px-6 t-pb-6": variant === "default",
            "t-px-6": variant === "primary",
            "t-shadow-light": shadow,
            "t-rounded": borderRounded === "sm",
            "t-rounded-lg": borderRounded === "lg",
            "t-rounded-xl": borderRounded === "xl",
          }
        )}
      />
    </Provider>
  );
};

type HeaderProps = HTMLAttributes<HTMLDivElement> & {
  bordered?: boolean;
  sticky?: boolean;
};

const Header = ({ bordered = true, sticky = true, ...props }: HeaderProps) => {
  const { variant } = useContext(Context);
  return (
    <div
      {...props}
      className={cx(
        "t-flex t-flex-shrink-0 t-items-center t-justify-between t-bg-surface",
        props.className || "",
        {
          "t-sticky t-top-0 t-z-header": sticky,
          "t-pb-3 t-pt-5": variant === "default" || variant === "primary",
          "t-px-6 t-py-4 sm:t-px-4": variant === "secondary",
          "t-border-0 t-border-b t-border-solid t-border-b-neutral-0": bordered,
        }
      )}
    />
  );
};

const Title = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      {...props}
      className={cx(
        "t-text-body-sm t-font-medium t-text-text-100",
        props.className || ""
      )}
    />
  );
};

const Body = (props: HTMLAttributes<HTMLDivElement>) => {
  const { variant } = useContext(Context);

  return (
    <div
      {...props}
      className={cx(props.className || "", {
        "t-mt-5": variant === "default" || variant === "primary",
        "t-px-4 t-py-5": variant === "secondary",
      })}
    />
  );
};

const Card = {
  Root,
  Header,
  Title,
  Body,
};

export default Card;
